import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Call, CallEnd, PhoneInTalk } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(0.5)
  },
  compactButton: {
    color: theme.mode === "dark" ? "#dfefff" : theme.palette.primary.main
  },
  helperText: {
    marginBottom: theme.spacing(2),
    opacity: 0.82
  },
  actionsMobile: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  infoCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    background: theme.palette.action.hover
  }
}));

const normalizeDialTarget = (value = "") =>
  String(value || "")
    .trim()
    .replace(/[^\d+]/g, "")
    .replace(/(?!^)\+/g, "");

const getContactNumber = (contact) => {
  if (!contact) return "";
  return normalizeDialTarget(contact.number || contact.phone || contact.whatsapp || "");
};

const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    window.navigator.userAgent || ""
  );

const ZADARMA_RUNTIME_URL = "https://my.zadarma.com/webphoneWebRTCWidget/v8/js/loader-phone-lib.js?v=23";

const loadScript = (src, id) =>
  new Promise((resolve, reject) => {
    const existing = id ? document.getElementById(id) : null;

    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve(existing);
        return;
      }

      existing.addEventListener("load", () => resolve(existing), { once: true });
      existing.addEventListener("error", () => reject(new Error(`No se pudo cargar ${src}`)), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    if (id) {
      script.id = id;
    }
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve(script);
    };
    script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.body.appendChild(script);
  });

const waitForGlobal = (checker, timeout = 15000) =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const verify = () => {
      if (checker()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeout) {
        reject(new Error("Zadarma WebRTC no termino de cargar a tiempo."));
        return;
      }

      window.setTimeout(verify, 200);
    };

    verify();
  });

const Softphone = ({ contact }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const phoneRef = useRef(null);
  const webrtcKeyRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [dialNumber, setDialNumber] = useState(getContactNumber(contact));
  const [operatorNumber, setOperatorNumber] = useState("");
  const [browserPhoneReady, setBrowserPhoneReady] = useState(false);
  const [callState, setCallState] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("Listo para iniciar la llamada.");
  const [mobileWebrtcBlocked, setMobileWebrtcBlocked] = useState(isMobileDevice());
  const isMobile = useMemo(() => isMobileDevice(), []);
  const hasMobileCallbackNumber = Boolean(
    String(userSettings?.telephonyCallbackNumber || "").trim()
  );

  useEffect(() => {
    setDialNumber(getContactNumber(contact));
  }, [contact]);

  useEffect(() => {
    setOperatorNumber(String(userSettings?.telephonyCallbackNumber || ""));
  }, [userSettings]);

  const fetchSettings = useCallback(async () => {
    if (!user?.companyId || !user?.id) return;

    try {
      const [{ data: settings }, { data: userData }] = await Promise.all([
        api.get(`/companySettings/${user.companyId}`),
        api.get(`/users/${user.id}`)
      ]);

      setCompanySettings(settings);
      setUserSettings(userData);
    } catch (error) {
      toast.error("No se pudo cargar la configuracion de telefonia.");
    }
  }, [user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const canUseTelephony = useMemo(() => {
    return (
      companySettings?.telephonyEnabled === "enabled" &&
      companySettings?.telephonyShowOnTicket !== "disabled" &&
      companySettings?.telephonyProvider === "zadarma" &&
      userSettings?.telephonyEnabled === true &&
      userSettings?.telephonyAllowOutgoing === true
    );
  }, [companySettings, userSettings]);

  const handleBrowserPhoneStatus = useCallback((message, parameters = {}) => {
    switch (message) {
      case "registered":
      case "connected":
        setBrowserPhoneReady(true);
        setCallState((previous) => (previous === "incoming" || previous === "outgoing" || previous === "in-call" ? previous : "ready"));
        setStatusMessage("Linea conectada.");
        break;
      case "outgoing":
        setCallState("outgoing");
        setStatusMessage(`Llamando a ${parameters?.dst || dialNumber}...`);
        break;
      case "incoming":
        setCallState("incoming");
        setStatusMessage("Tiene una llamada entrante.");
        break;
      case "accepted":
        setStatusMessage("La llamada fue aceptada. Conectando audio...");
        break;
      case "confirmed":
        setCallState("in-call");
        setStatusMessage("Llamada en curso.");
        break;
      case "busy":
        setCallState("ready");
        setStatusMessage("La línea está ocupada.");
        break;
      case "rejected":
        setCallState("ready");
        setStatusMessage("La llamada fue rechazada.");
        break;
      case "canceled":
        setCallState("ready");
        setStatusMessage("La llamada finalizó o fue cancelada.");
        break;
      case "BROWSER_NOT_SUPPORTED":
      case "browserNotSupported":
        setBrowserPhoneReady(false);
        setCallState("error");
        setStatusMessage("Esta opcion no esta disponible en este dispositivo.");
        break;
      default:
        break;
    }
  }, [dialNumber, userSettings]);

  const ensureBrowserPhoneConnected = useCallback(async () => {
    if (mobileWebrtcBlocked) {
      throw new Error("Esta opcion no esta disponible en este dispositivo.");
    }

    if (phoneRef.current && browserPhoneReady) {
      return phoneRef.current;
    }

    if (!userSettings?.telephonyExtension) {
      throw new Error("La linea de llamadas no esta configurada para este usuario.");
    }

    setConnecting(true);
    setStatusMessage("Conectando linea...");

    try {
      await loadScript(ZADARMA_RUNTIME_URL, "zadarma-webrtc-loader");
      await waitForGlobal(() => (
        typeof window.ZadarmaWebphoneAPI === "function" &&
        typeof window.md5 === "function" &&
        typeof window.io === "function" &&
        typeof window.JsSIP !== "undefined" &&
        typeof window.DetectRTC !== "undefined" &&
        typeof window.ZDRMscriptDiv !== "undefined" &&
        document.getElementById("zdrm-webRTCRemoteView") &&
        document.getElementById("zdrm-incomingRing") &&
        document.getElementById("zdrm-outgoingRing")
      ));

      if (!webrtcKeyRef.current) {
        const { data } = await api.get("/telephony/zadarma/webrtc-key");
        webrtcKeyRef.current = data;
      }

      if (!phoneRef.current) {
        const phoneInstance = new window.ZadarmaWebphoneAPI();
        phoneInstance.init({
          type: "CRM",
          key: webrtcKeyRef.current.key,
          sip: webrtcKeyRef.current.login,
          language: "es",
          getStatusMessage: handleBrowserPhoneStatus
        });
        phoneRef.current = phoneInstance;
      }

      phoneRef.current.reg(webrtcKeyRef.current.login);
      setBrowserPhoneReady(true);
      setCallState("ready");
      setStatusMessage("Conectando linea...");
      return phoneRef.current;
    } catch (error) {
      setBrowserPhoneReady(false);
      setCallState("error");
      if (isMobileDevice()) {
        setMobileWebrtcBlocked(true);
      }
      const message = error?.response?.data?.error || error?.message || "No se pudo preparar la llamada.";
      setStatusMessage(message);
      throw error;
    } finally {
      setConnecting(false);
    }
  }, [browserPhoneReady, handleBrowserPhoneStatus, userSettings]);

  const handleDirectCall = async () => {
    setLoading(true);
    try {
      const normalizedNumber = normalizeDialTarget(dialNumber).replace(/[^\d]/g, "");
      if (!normalizedNumber) {
        throw new Error("Este contacto no tiene un numero valido para llamar.");
      }

      const browserPhone = await ensureBrowserPhoneConnected();
      browserPhone.call(normalizedNumber);
      setCallState("outgoing");
      setStatusMessage(`Iniciando llamada a ${normalizedNumber} desde el navegador...`);
      toast.success("Llamada iniciada.");
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || "No se pudo iniciar la llamada.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFallbackCallback = async () => {
    setLoading(true);
    try {
      const normalizedNumber = normalizeDialTarget(dialNumber).replace(/[^\d]/g, "");
      if (!normalizedNumber) {
        throw new Error("Este contacto no tiene un numero valido para llamar.");
      }

      const { data } = await api.post("/telephony/zadarma/callback", {
        number: normalizedNumber,
        callbackNumber: operatorNumber
      });

      toast.success(data?.message || "Llamada iniciada.");
      setStatusMessage(userSettings?.telephonyCallbackNumber
        ? "Revise el telefono configurado para recibir la llamada."
        : "La llamada fue enviada a su linea configurada.");
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || "No se pudo iniciar la llamada.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerCall = () => {
    if (!phoneRef.current) {
      return;
    }

    phoneRef.current.answer();
    setStatusMessage("Contestando llamada...");
  };

  const handleHangup = () => {
    if (!phoneRef.current) {
      return;
    }

    phoneRef.current.finishCall();
    setCallState("ready");
    setStatusMessage("Llamada finalizada.");
  };

  useEffect(() => {
    if (!open || !userSettings?.telephonyAutoConnect) {
      return undefined;
    }

    if (!mobileWebrtcBlocked) {
      ensureBrowserPhoneConnected().catch(() => {});
    }
    return undefined;
  }, [ensureBrowserPhoneConnected, mobileWebrtcBlocked, open, userSettings]);

  useEffect(() => () => {
    if (phoneRef.current?.unreg) {
      try {
        phoneRef.current.unreg();
      } catch (_error) {
        // Ignorado para no romper el cierre de la vista.
      }
    }
  }, []);

  if (!companySettings || !userSettings || !canUseTelephony) {
    return null;
  }

  return (
    <>
      <div className={classes.toolbar}>
        <Tooltip title="Llamar al cliente">
          <IconButton
            size="small"
            className={classes.compactButton}
            onClick={() => setOpen(true)}
          >
            <PhoneInTalk fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Llamadas</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" className={classes.helperText}>
            {isMobile
              ? "Toque llamar para que el sistema lo comunique con el cliente."
              : "Puede iniciar la llamada desde aqui."}
          </Typography>

          <div className={classes.infoCard}>
            <Typography variant="subtitle2" gutterBottom>
              Linea activa
            </Typography>
            <Typography variant="body2">
              {userSettings?.telephonyDisplayName || user.name} - {userSettings?.telephonyExtension || "sin extension"}
            </Typography>
            {isMobile && Boolean(userSettings?.telephonyCallbackNumber) && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Sonara en: {operatorNumber || userSettings.telephonyCallbackNumber}
              </Typography>
            )}
            {isMobile && !hasMobileCallbackNumber && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Falta configurar el numero del operador para recibir esta llamada.
              </Typography>
            )}
            <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
              {statusMessage}
            </Typography>
          </div>

          <TextField
            label="Numero del contacto"
            fullWidth
            variant="outlined"
            size="small"
            value={dialNumber}
            onChange={(event) => setDialNumber(event.target.value)}
            helperText="Puede cambiar el numero antes de llamar."
          />
          {isMobile && (
            <TextField
              label="Numero donde usted recibira la llamada"
              fullWidth
              variant="outlined"
              size="small"
              value={operatorNumber}
              onChange={(event) => setOperatorNumber(event.target.value)}
              helperText="Puede cambiarlo solo para esta llamada."
              style={{ marginTop: 12 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {isMobile ? (
            <div className={classes.actionsMobile}>
              <Button onClick={() => setOpen(false)}>Cerrar</Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<Call />}
                onClick={handleFallbackCallback}
                disabled={loading || connecting}
              >
                {loading ? "Llamando..." : "Llamar"}
              </Button>
            </div>
          ) : (
            <>
              <Button
                onClick={ensureBrowserPhoneConnected}
                disabled={connecting || loading || mobileWebrtcBlocked}
              >
                {connecting ? "Conectando..." : browserPhoneReady ? "Reconectar" : "Conectar"}
              </Button>
              {callState === "incoming" && (
                <Button
                  color="primary"
                  onClick={handleAnswerCall}
                  disabled={loading || connecting}
                >
                  Contestar
                </Button>
              )}
              {(callState === "outgoing" || callState === "in-call" || callState === "incoming") && (
                <Button
                  color="secondary"
                  startIcon={<CallEnd />}
                  onClick={handleHangup}
                >
                  Colgar
                </Button>
              )}
              <Button onClick={() => setOpen(false)}>Cerrar</Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<Call />}
                onClick={handleDirectCall}
                disabled={loading || connecting || mobileWebrtcBlocked}
              >
                {loading ? "Llamando..." : "Llamar al cliente"}
              </Button>
              <Button onClick={handleFallbackCallback} disabled={loading || connecting}>
                Respaldo
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Softphone;
